import React, { useMemo } from "react";
import { FiArrowDown } from "react-icons/fi";
import { useTranslation } from "react-i18next";
import "./AllSectorsHero.css";
import heroBg from "../../../../assets/images/allSectors/allsehero.png";
import { scrollToElementById } from "../../../../utils/scrollToId";
import { resolveMediaUrl } from "../../../../utils/media";
import { usePageContentAliases } from "../../../../hooks/usePublicQueries";

const fallbackAllSectorsHero = {
  title: "Our sectors",
  description:
    "Our solutions support key sectors by offering durable and high-quality systems, suitable for a wide range of applications and project requirements",
  ctaText: "View all sectors",
};

const pickString = (...candidates) => {
  for (const c of candidates) {
    if (typeof c === "string" && c.trim()) return c.trim();
  }
  return "";
};

const hasSectionData = (section) => {
  if (!section || typeof section !== "object") return false;
  return Object.values(section).some((value) => {
    if (value === null || typeof value === "undefined") return false;
    if (typeof value === "string") return value.trim().length > 0;
    return true;
  });
};

const tryParseJsonObject = (str) => {
  const t = String(str || "").trim();
  if (!t.startsWith("{") || !t.endsWith("}")) return null;
  try {
    const o = JSON.parse(t);
    return o && typeof o === "object" && !Array.isArray(o) ? o : null;
  } catch {
    return null;
  }
};

const coalesceHeroPayload = (raw) => {
  if (raw == null) return null;
  if (typeof raw === "string") return tryParseJsonObject(raw) || null;
  if (typeof raw !== "object") return null;
  let base = { ...raw };
  if (raw.data && typeof raw.data === "object" && !Array.isArray(raw.data)) {
    base = { ...raw, ...raw.data };
  }
  if (typeof raw.value === "string") {
    const parsed = tryParseJsonObject(raw.value);
    if (parsed) base = { ...base, ...parsed };
  } else if (raw.value && typeof raw.value === "object" && !Array.isArray(raw.value)) {
    base = { ...base, ...raw.value };
  }
  return base;
};

const normalizeSectionSlug = (key) =>
  String(key || "")
    .toLowerCase()
    .replace(/[_\s-]+/g, "");

const findPageSection = (root, slug) => {
  if (!root || typeof root !== "object") return undefined;
  const target = normalizeSectionSlug(slug);
  const hit = Object.keys(root).find((k) => normalizeSectionSlug(k) === target);
  return hit != null ? root[hit] : undefined;
};

const SECTORS_PAGE_ALIASES = ["allsectors", "all-sectors", "sectors", "allsector"];

const AllSectorsHero = ({ content }) => {
  const { i18n } = useTranslation();
  const shouldFetch = !(content && typeof content === "object");
  const { data: pageContent } = usePageContentAliases(SECTORS_PAGE_ALIASES, i18n.language, {
    enabled: shouldFetch,
  });

  const apiContent = useMemo(() => {
    if (!shouldFetch) return null;
    return (
      findPageSection(pageContent, "hero") ||
      findPageSection(pageContent, "allsectorshero") ||
      findPageSection(pageContent, "allsectors") ||
      null
    );
  }, [pageContent, shouldFetch]);

  const view = useMemo(() => {
    const source = content ?? apiContent;
    const merged = coalesceHeroPayload(source);
    const section = hasSectionData(merged) ? merged : fallbackAllSectorsHero;

    return {
      title: pickString(section.title, section.heading) || fallbackAllSectorsHero.title,
      description:
        pickString(section.description, section.body, section.text, section.summary, section.content) ||
        fallbackAllSectorsHero.description,
      ctaText:
        pickString(section.ctaText, section.linkText, section.exploreText, section.buttonText) ||
        fallbackAllSectorsHero.ctaText,
      backgroundImage:
        resolveMediaUrl(
          section.backgroundImage ||
            section.bgImage ||
            section.image ||
            section.heroImage ||
            section.mediaUrl ||
            section.photo
        ) || heroBg,
    };
  }, [content, apiContent]);

  const handleViewSectors = () => {
    scrollToElementById("sectors-explore");
  };

  return (
    <section
      className="allsectors-hero"
      style={{ backgroundImage: `url(${view.backgroundImage})` }}
    >
      <div className="allsectors-overlay"></div>

      <div className="allsectors-container">
        <h1>{view.title}</h1>

        <p>{view.description}</p>

        <button type="button" className="buttongyp" onClick={handleViewSectors}>
          {view.ctaText} <FiArrowDown className="icon" />
        </button>
      </div>
    </section>
  );
};

export default AllSectorsHero;
